/**
 * Colour per tool namespace.
 *
 * 415 tool calls on a single run come from a handful of namespaces, so hue is
 * the cheapest way to make a long transcript scannable: an operator learns
 * "blue = browser, violet = memory, green = issues" in one pass and can then
 * find the shape of a run without reading it.
 *
 * Class names are written out in full on purpose — Tailwind's JIT scans source
 * text, so a built-up `text-${hue}-600` is purged from the stylesheet and the
 * colour silently disappears in the production build.
 */

const THEMES = {
  browsing: {
    label: "browser",
    dot: "text-sky-500",
    nsText: "text-sky-600",
    rail: "border-sky-200",
    chipBg: "bg-sky-50",
    chipText: "text-sky-800",
  },
  memory: {
    label: "memory",
    dot: "text-violet-500",
    nsText: "text-violet-600",
    rail: "border-violet-200",
    chipBg: "bg-violet-50",
    chipText: "text-violet-800",
  },
  issues: {
    label: "issues",
    dot: "text-emerald-500",
    nsText: "text-emerald-600",
    rail: "border-emerald-200",
    chipBg: "bg-emerald-50",
    chipText: "text-emerald-800",
  },
  "fan-out": {
    label: "fan-out",
    dot: "text-amber-500",
    nsText: "text-amber-600",
    rail: "border-amber-200",
    chipBg: "bg-amber-50",
    chipText: "text-amber-800",
  },
  evidence: {
    label: "evidence",
    dot: "text-pink-500",
    nsText: "text-pink-600",
    rail: "border-pink-200",
    chipBg: "bg-pink-50",
    chipText: "text-pink-800",
  },
  "write-skill": {
    label: "skills",
    dot: "text-indigo-500",
    nsText: "text-indigo-600",
    rail: "border-indigo-200",
    chipBg: "bg-indigo-50",
    chipText: "text-indigo-800",
  },
};

const DEFAULT_THEME = {
  label: "other",
  dot: "text-gray-400",
  nsText: "text-gray-500",
  rail: "border-gray-200",
  chipBg: "bg-gray-100",
  chipText: "text-gray-700",
};

const ERROR_THEME = {
  label: "error",
  dot: "text-red-500",
  nsText: "text-red-600",
  rail: "border-red-300",
  chipBg: "bg-red-50",
  chipText: "text-red-800",
};

export function toolNamespace(name) {
  const idx = (name || "").indexOf(":");
  return idx > 0 ? name.slice(0, idx) : null;
}

export function toolTheme(name, isError) {
  if (isError) return ERROR_THEME;
  return THEMES[toolNamespace(name)] || DEFAULT_THEME;
}

/** Namespaces present in a run, for the legend. */
export function usedNamespaces(nodes) {
  const seen = new Set();
  const walk = (list) => {
    for (const n of list || []) {
      for (const t of n.turns || []) {
        for (const it of t.items) {
          if (it.kind === "tool") {
            const ns = toolNamespace(it.name);
            if (ns && THEMES[ns]) seen.add(ns);
          }
        }
      }
      walk(n.children);
    }
  };
  walk(nodes);
  return [...seen].map((ns) => ({ ns, ...THEMES[ns] }));
}
